/* eslint-disable react-hooks/exhaustive-deps */
// SellerNetSheet/hooks/useSellerNetEngine.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getTitleEscrowCosts,
  getStateSettings,
  getNationalSettings,
  getCityStateZipCode,
} from "../services/titleEscrowService";
import { getSellerCalc, getUserTemplate } from "../services/sellerNetService";
import {
  computeAllTotals,
  updateDerivedFields,
  getSellerEstimatedTax,
  calculateAdjusted,
  computeWashingtonSellerExciseTaxes,
  computeTitleFeePayorAmount,
} from "../utils/calculations";
import { parseCurrency, getDefaultEstDate } from "../sellerNet.shared";

const isHomeWarrantyItem = (item) => {
  const key = (item?.key || "").toLowerCase();
  const label = (item?.label || "").toLowerCase();
  const camelKey = (item?.camelKey || "").toLowerCase();
  return (
    (label.includes("home") &&
      (label.includes("warrant") || label.includes("warranty"))) ||
    key.includes("home_warranty") ||
    key.includes("homewarranty") ||
    camelKey.includes("homewarranty")
  );
};

export const useSellerNetEngine = ({
  // ids/config
  mainId,
  userId,
  companyId,
  postalCode,
  mainCompanyName,
  calcId,
  isQuickMode,
  user,
  // rhf
  watch,
  getValues,
  setValue,
  reset,
  arrays,
  replaceArray,
  refs,

  // toast
  showToast,
}) => {
  const defaultEst = useMemo(() => getDefaultEstDate(), []);

  // UI/business state
  const [ttbData, setTtbData] = useState({});
  const [templateMissing, setTemplateMissing] = useState(false);
  const [nationalSettingsMap, setNationalSettingsMap] = useState({});

  const [showPreviewFromChild, setShowPreviewFromChild] = useState(false);
  const [showManualEditor, setShowManualEditor] = useState(false);
  const [showAddressView, setShowAddressView] = useState(true);
  const [isCorrectProperty, setIsCorrectProperty] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [useMortgageBalance, setUseMortgageBalance] = useState(false);
  const [useAnnualPropertyTax, setUseAnnualPropertyTax] = useState(true);
  const [useHomeWarranty, setUseHomeWarranty] = useState(false);

  // watched
  const addressStateId = watch("addressMeta.stateid");
  const watchedStateSettings = watch("stateSettings");
  const watchedAnnualPropertyTax = watch(
    "loanInfoMeta.annualPropertyTaxAmount",
  );
  const watchedEstimatedClosingDate = watch(
    "loanInfoMeta.estimatedClosingDate",
  );
  const watchedLoanType = watch("loanInfoMeta.buyersLoanType");
  const watchedHomeWarrantyAmount = watch("loanInfoMeta.homeWarrantyAmount");
  watch("loanInfoMeta.autoEstimateLoan"); // watched for reactivity (debug block commented out)
  /*
    // Debug: log when autoEstimateLoan or related form data changes
    useEffect(() => {
      const loanInfo = getValues("loanInfoMeta") || {};
      console.log("[Seller autoEstimateLoan] form value:", watchedAutoEstimateLoan, {
        autoEstimateLoan: loanInfo.autoEstimateLoan,
        loansToBePaidBalance1: loanInfo.loansToBePaidBalance1,
        loansToBePaidRate1: loanInfo.loansToBePaidRate1,
        mortgageAmount: loanInfo.mortgageAmount,
      });
    }, [watchedAutoEstimateLoan, getValues]);
  */
  // container interaction -> totals (RAF debounce)
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  const calculateTotals = useCallback(() => {
    const totalsAgg = computeAllTotals(getValues());
    setValue("totalClosingMeta", {
      titleSettlementMetaTotal: totalsAgg.titleSettlementMetaTotal,
      sellerClosingCostMetaTotal: totalsAgg.sellerClosingCostMetaTotal,
      compensationMetaTotal: totalsAgg.compensationMetaTotal,
      closingCostMetaTotal: totalsAgg.closingCostMetaTotal,
      sellerOtherCostMetaTotal: totalsAgg.sellerOtherCostMetaTotal,
      sellerCostsTotal: totalsAgg.sellerCostsTotal,
      otherCostsTotal: totalsAgg.otherCostsTotal,
      allCostsTotal: totalsAgg.allCostsTotal,
      estimatedSellerNet: totalsAgg.estimatedSellerNet,
    });
  }, [getValues, setValue]);

  const debouncedTotals = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => calculateTotals());
  }, [calculateTotals]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onInteraction = () => debouncedTotals();
    el.addEventListener("change", onInteraction);
    el.addEventListener("blur", onInteraction, true);

    return () => {
      el.removeEventListener("change", onInteraction);
      el.removeEventListener("blur", onInteraction, true);
    };
  }, [debouncedTotals]);

  // national settings
  useEffect(() => {
    if (!mainId) return;
    (async () => {
      try {
        const res = await getNationalSettings({ maincompanyid: mainId });
        const nationalData = Array.isArray(res?.data) ? res.data[0] : {};
        const fha = nationalData?.fha_closing_costs || {};
        const va = nationalData?.va_closing_costs || {};
        const usda = nationalData?.usda_closing_costs || {};

        const ranges = fha?.loan_amount_factor?.ranges || [];
        const byLabel = (lbl) =>
          ranges.find((r) => String(r?.label || "").toLowerCase() === lbl);

        const under = byLabel("under");
        const to = byLabel("to");
        const over = byLabel("over");

        setNationalSettingsMap({
          VA_FundingFee: va?.mortgage_insurance?.funding_fee_percent ?? null,
          USDA_MIPFactor: usda?.loan_amount_factor?.mip_factor_percent ?? null,
          FHA_PercentOne: fha?.mortgage_insurance?.years?.[0]?.percent ?? null,
          FHA_SalePriceUnder: under?.sale_price ?? null,
          FHA_SalePriceUnderLTV: under?.ltv_percent ?? null,
          FHA_SalePriceTo: to?.sale_price ?? null,
          FHA_SalePriceToLTV: to?.ltv_percent ?? null,
          FHA_SalePriceOver: over?.sale_price ?? null,
          FHA_SalePriceOverLTV: over?.ltv_percent ?? null,
        });
      } catch (e) {
        console.warn("national settings failed", e);
      }
    })();
  }, [mainId]);

  // GET /state-setting — seller proration rules for current property state
  const fetchSellerStateSettings = useCallback(
    async (forcedStateId) => {
      if (!mainId || refs.isPopulatingRef.current) return;
      const fromForm =
        typeof getValues === "function"
          ? getValues("addressMeta.stateid")
          : "";
      const stateid =
        forcedStateId != null && String(forcedStateId).trim() !== ""
          ? String(forcedStateId).trim()
          : fromForm
            ? String(fromForm).trim()
            : "";
      if (!stateid) return;
      try {
        const res = await getStateSettings({
          stateid,
          type: "seller_proration",
          maincompanyid: mainId,
        });
        if (res?.data) setValue("stateSettings", res.data);
      } catch {
        // silent
      }
    },
    [mainId, getValues, setValue, refs.isPopulatingRef],
  );

  /** Recompute %-of-sale / %-of-loan rows from current form (matches sales-price blur). */
  const applyDerivedCostRows = useCallback(() => {
    if (refs.isPopulatingRef.current) return;
    const form = getValues() || {};
    const updated = updateDerivedFields(form);
    if (Array.isArray(updated.compensationMeta))
      replaceArray("compensation", updated.compensationMeta);
    if (Array.isArray(updated.otherCostMeta))
      replaceArray("otherCost", updated.otherCostMeta);
    if (Array.isArray(updated.closingCostMeta))
      replaceArray("closingCost", updated.closingCostMeta);
    if (Array.isArray(updated.sellerClosingCostMeta))
      replaceArray("sellerClosingCost", updated.sellerClosingCostMeta);
    calculateTotals();
  }, [getValues, replaceArray, calculateTotals, refs.isPopulatingRef]);

  useEffect(() => {
    if (!addressStateId || !mainId || refs.isPopulatingRef.current) return;
    fetchSellerStateSettings(addressStateId);
  }, [addressStateId, mainId, fetchSellerStateSettings]);

  // title/escrow
  const fetchTitleEscrow = useCallback(
    async (form) => {
      if (refs.isPopulatingRef.current) return;

      const am = form?.addressMeta || {};
      const closing_state = String(
        form?.state ?? am.state ?? am.closing_state ?? "",
      );
      const closing_city = String(form?.city ?? am.city ?? "");
      const closing_county = String(
        form?.closing_county ??
        form?.county ??
        am.closing_county ??
        am.county ??
        "",
      );

      const payload = {
        closing_state,
        closing_city,
        closing_county,
        county: closing_county,
        city: closing_city,
        zip: form?.addressMeta?.zip || form?.zip || "",
        salePrice: form?.salesPrice,
        adjusted: form?.loanInfoMeta?.adjusted,
        loanType: form?.loanInfoMeta?.buyersLoanType || form?.buyersLoanType,
        estStlmtDate: form?.loanInfoMeta?.estimatedClosingDate,
        userId,
        device: "web",
        calc: "seller",
        reissueyr: false,
        mrs_id: "Gf40rntTRu",
        prepared_for: form?.preparedFor,
        liab_amount: "0.00",
        sim: true,
      };

      const params = { mainid: mainId, userid: userId, companyid: companyId };
      const res = await getTitleEscrowCosts(payload, params);
      //  console.log("Title/Escrow API response:", res);
      const d = res?.data;
      if (!d) return;

      const escrowSettlementAmount =
        d.escrowType === "Split"
          ? (d.escrowFeeBuyer ?? "0.00")
          : d.escrowType === "Seller"
            ? (d.escrowFee ?? "0.00")
            : "0.00";

      const ownersTitleAmount =
        d.ownerType === "Split"
          ? parseCurrency(d.ownerFee) / 2
          : d.ownerType === "Seller"
            ? (d.ownerFee ?? "0.00")
            : "0.00";

      const lendersTitleAmount =
        d.lenderType === "Split"
          ? parseCurrency(d.lenderFee) / 2
          : d.lenderType === "Seller"
            ? (d.lenderFee ?? "0.00")
            : "0.00";

      setValue("titleSettlementMeta", {
        ownersTitleAmount,
        ownersTitleSeller: d.ownerFee ?? "0.00",
        ownersTitlePayor: d.ownerType ?? "Seller",
        escrowSettlementPayor: d.escrowType ?? "Seller",
        escrowSettlementAmount,
        escrowSettlementSeller: d.escrowFee ?? "0.00",
        escrowSettlementSplit: d.escrowFeeSeller ?? "0.00",
        lendersTitleAmount,
        lendersTitleSeller: d.lenderFee ?? "0.00",
        lendersTitlePayor: d.lenderType ?? "Seller",
        salesTaxRate: d.salesTaxRate ?? "0.00",
      });

      let checkState = getValues("addressMeta.state") || closing_state;

      const salesTaxRate = Number(d.salesTaxRate ?? "0.00") || 0;
      // Metro United Title & Escrow Company
      if (String(mainId) === "5" && checkState === 'WA' && salesTaxRate !== 0) {
        //For Washington, the Seller pays for OTP and Split Escrow, so I have a line item for Sales Tax - Title and another for Sales Tax - Escrow for the Seller Net sheet.  So using your example below, Sales Tax - Title line item should show $168.00 on the Seller’s module.
        const combinedTitleSalesTax =
          (Number(ownersTitleAmount) || 0) * salesTaxRate;
        setValue("closingCostMeta.9.value", combinedTitleSalesTax);
        setValue && setValue("closingCostMeta.9.type", "Flat Fee");

        const combinedEscrowSalesTax =
          (Number(escrowSettlementAmount) || 0) * salesTaxRate;
        setValue("closingCostMeta.10.value", combinedEscrowSalesTax);
        setValue && setValue("closingCostMeta.10.type", "Flat Fee");
      }

      // CA city/county transfer tax (and any future backend-resolved closing-cost row) —
      // same direct-index overwrite as the WA sales-tax rows above, but the row position
      // and content are fully decided by the backend (closingCostOverrideService), so a
      // new one is a backend-only change — nothing here has to grow. Like the WA rows,
      // this only overwrites a slot the field array already has (setValue on an index
      // past its current length won't register with useFieldArray's own tracked state).
      const existingClosingCostMeta = getValues("closingCostMeta");
      const existingClosingCostCount = Array.isArray(existingClosingCostMeta)
        ? existingClosingCostMeta.length
        : 0;
      // "title" mode: on-demand rows for EscrowTitleFields.js (e.g. CA City/County Transfer
      // Tax) — not part of the fixed closingCostMeta template, rendered/edited like
      // Owners/Lenders Title Policy: a Payor select (Split/Buyer/Seller) that recomputes the
      // amount (see computeTitleFeePayorAmount), counted in titleSettlementMetaTotal.
      const titleRows = [];
      // "closing" mode: on-demand rows for ManualSheetSection's sellerClosingCostMeta field
      // array. Unlike closingCostMeta (a fixed-length template, overwritten by sr_no index),
      // this array has no position concept — rows are upserted by key via the field array's
      // own append/update methods (not raw setValue), so useFieldArray's internal tracking
      // stays correct when a brand-new row needs to be appended rather than just overwritten.
      const existingSellerClosingCostMeta = getValues("sellerClosingCostMeta") || [];
      if (Array.isArray(d.closingCostOverrides)) {
        d.closingCostOverrides.forEach((row) => {
          if (row.mode === 'title') {
            const baseAmount = Number(row.value) || 0;
            const payor = row.payor || "";
            titleRows.push({
              label: row.label,
              payor,
              baseAmount,
              amount: computeTitleFeePayorAmount(payor, baseAmount),
            });
          } else if (row.mode === 'closing') {
            if (!row?.key) return;
            const idx = existingSellerClosingCostMeta.findIndex((it) => it?.key === row.key);
            if (idx !== -1) {
              arrays.sellerClosingCost.update(idx, { ...existingSellerClosingCostMeta[idx], ...row });
            } else {
              arrays.sellerClosingCost.append(row);
            }
          } else {
            if (row?.sr_no == null || row.sr_no >= existingClosingCostCount) return;
            const base = `closingCostMeta.${row.sr_no}`;
            setValue(`${base}.key`, row.key);
            setValue(`${base}.label`, row.label);
            setValue(`${base}.type`, row.type);
            setValue(`${base}.unit`, row.unit);
            setValue(`${base}.value`, row.value);
            setValue(`${base}.total`, row.total);
            setValue(`${base}.Cash`, row.Cash);
          }
        });
        setValue("titleSettlementMeta.transferTaxRows", titleRows);
      }
    },
    [mainId, userId, companyId, setValue, getValues, arrays, refs.isPopulatingRef],
  );

  // adjusted loan calc
  const computeAdjustedForLoanType = useCallback(
    (salePriceVal, loanTypeVal) => {
      if (refs.isPopulatingRef.current) return;

      const salePrice = parseCurrency(salePriceVal);
      if (salePrice <= 0) return;

      const loanType = String(
        loanTypeVal || getValues("loanInfoMeta.buyersLoanType") || "",
      );
      const lt = loanType.toLowerCase();

      const req = { salePrice, loanType };

      if (lt.includes("va")) req.LTV = nationalSettingsMap?.VA_FundingFee ?? 0;
      else if (lt.includes("usda"))
        req.MIP = nationalSettingsMap?.USDA_MIPFactor ?? 0;
      else if (lt.includes("conventional")) {
        req.LTV = Number(getValues("loanInfoMeta.conventionalPercent")) || 0;
        req.MIP = Number(getValues("loanInfoMeta.MIP")) || 0;
      } else if (lt.includes("fha")) {
        req.MIP = nationalSettingsMap?.FHA_PercentOne ?? 0;
        req.FHA = {
          FHA_SalePriceUnder: nationalSettingsMap?.FHA_SalePriceUnder ?? 0,
          FHA_SalePriceUnderLTV:
            nationalSettingsMap?.FHA_SalePriceUnderLTV ?? 0,
          FHA_SalePriceTo: nationalSettingsMap?.FHA_SalePriceTo ?? 0,
          FHA_SalePriceToLTV: nationalSettingsMap?.FHA_SalePriceToLTV ?? 0,
          FHA_SalePriceOver: nationalSettingsMap?.FHA_SalePriceOver ?? 0,
          FHA_SalePriceOverLTV: nationalSettingsMap?.FHA_SalePriceOverLTV ?? 0,
        };
      }

      const res = calculateAdjusted(req);
      const prev = getValues("loanInfoMeta") || {};
      setValue("loanInfoMeta", {
        ...prev,
        amount: res.amount ?? 0,
        adjusted: res.adjusted ?? 0,
      });
    },
    [getValues, setValue, refs.isPopulatingRef, nationalSettingsMap],
  );

  // sales price blur pipeline
  const handleSalesPriceBlur = useCallback(
    async (rawValue) => {
      if (refs.isPopulatingRef.current) return;

      const sp = getValues("salesPrice") || rawValue || 0;
      const spNum = parseCurrency(sp);
      const lt =
        watchedLoanType ||
        getValues("loanInfoMeta.buyersLoanType") ||
        "Conventional";

      if (spNum > 0) computeAdjustedForLoanType(spNum, lt);

      const current = getValues();
      if (spNum > 0) {
        await fetchTitleEscrow(current);
      }

      const snapshot = getValues() || {};
      let updated = updateDerivedFields(snapshot);

      // Metro United: Washington Excise Taxe
      const stateCode = String(snapshot?.addressMeta?.state || "").trim().toUpperCase();
      const isWaMainCompany = spNum > 0 && stateCode === "WA" && (String(mainId) === "5" || Number(mainId) === 5);

      if (isWaMainCompany &&
        Array.isArray(updated.closingCostMeta) &&
        updated.closingCostMeta.length > 8) {
        const { localExcise, stateExcise } = computeWashingtonSellerExciseTaxes(spNum);
        const stateStr = stateExcise.toFixed(2);
        const localStr = localExcise.toFixed(2);

        updated = {
          ...updated,
          closingCostMeta: updated.closingCostMeta.map((row, i) => {
            if (i === 7) {
              return {
                ...row,
                type: "Flat Fee",
                value: stateStr,
                total: stateExcise,
              };
            }
            if (i === 8) {
              return {
                ...row,
                type: "Flat Fee",
                value: localStr,
                total: localExcise,
              };
            }
            return row;
          }),
        };
      }

      if (Array.isArray(updated.compensationMeta))
        replaceArray("compensation", updated.compensationMeta);
      if (Array.isArray(updated.otherCostMeta))
        replaceArray("otherCost", updated.otherCostMeta);
      if (Array.isArray(updated.closingCostMeta)) {
        replaceArray("closingCost", updated.closingCostMeta);
        if (isWaMainCompany && updated.closingCostMeta.length > 8) {
          setValue("closingCostMeta.7.value", updated.closingCostMeta[7].value);
          setValue("closingCostMeta.7.type", "Flat Fee");
          setValue("closingCostMeta.8.value", updated.closingCostMeta[8].value);
          setValue("closingCostMeta.8.type", "Flat Fee");
        }
      }
      if (Array.isArray(updated.sellerClosingCostMeta))
        replaceArray("sellerClosingCost", updated.sellerClosingCostMeta);

      calculateTotals();
    },
    [
      refs.isPopulatingRef,
      getValues,
      setValue,
      mainId,
      watchedLoanType,
      computeAdjustedForLoanType,
      fetchTitleEscrow,
      replaceArray,
      calculateTotals,
    ],
  );

  // quick mode
  useEffect(() => {
    if (!isQuickMode) {
      setShowAddressView(true);
      return;
    }
    setShowAddressView(false);
    (async () => {
      setValue("loanInfoMeta.estimatedClosingDate", defaultEst);
      setValue("loanInfoMeta.loansToBePaidBalance1", "0.00");
      setValue("loanInfoMeta.loansToBePaidRate1", "0.00");
      setValue("loanInfoMeta.autoEstimateLoan", false);
      //   console.log("[Seller autoEstimateLoan] set by: quick mode -> false");

      try {
        const res = await getCityStateZipCode({
          zip: postalCode || "",
        });
        if (res?.status === "fail") {
          showToast({
            message:
              "The address you entered is incorrect or the State/County is not supported.",
            type: "error",
          });
          return;
        }
        const prevAddr =
          typeof getValues === "function" ? getValues("addressMeta") || {} : {};
        const zipPayload = res?.data ?? {};

        // Quick mode: do not set addressMeta.companyid from ZIP. The shell uses
        // user.companyid for template loading; setting a different companyid here
        // triggers a second getUserTemplate + full array replace and overwrites
        // labels/rows. Normal (non-quick) address enrichment still sets companyid.
        setValue("addressMeta", {
          ...prevAddr,
          city: zipPayload.city || "",
          state: zipPayload.state_code || "",
          stateid: zipPayload.stateid || "",
          county: zipPayload.county_name || "",
          countyid: zipPayload.countyid || "",
          zip: postalCode || "",
          unitNumber: zipPayload.site_unit || "",
          addressIncludesUnit: zipPayload.addressIncludesUnit || "",
          closing_county: zipPayload.closing_county || "",
          closing_state: zipPayload.closing_state || "",
          companyid: prevAddr.companyid || user?.companyid || "",
        });

        // Extract and set tax amount from response for tax prorations calculation
        const taxAmount = res?.data?.ttb?.taxAmount || 0;
        if (taxAmount > 0) {
          setValue("loanInfoMeta.annualPropertyTax", taxAmount);
          setValue("loanInfoMeta.annualPropertyTaxAmount", taxAmount);
        }
        // Set ttbData whenever API returns ttb (for tax and/or loan balance)
        const ttb = res?.data?.ttb;
        let useAutoEstimateFromBalance = false;
        if (ttb) {
          setTtbData({ taxAmount: taxAmount || ttb.taxAmount, ...ttb });
          // Prefill loan balance/rate from API when available
          const loanBal = ttb.loanBalance;
          if (loanBal != null) {
            const bal =
              loanBal?.balance ??
              (typeof loanBal === "string" || typeof loanBal === "number"
                ? loanBal
                : "0.00");
            const rate = loanBal?.interestRate ?? "0.00";
            setValue("loanInfoMeta.loansToBePaidBalance1", String(bal));
            setValue("loanInfoMeta.loansToBePaidRate1", String(rate));
            //    console.log(
            //     "[Seller city-state-zip API] set loansToBePaidBalance1/loansToBePaidRate1 from ttb:",
            //     { bal, rate },
            //    );
            useAutoEstimateFromBalance = Number(bal) > 0;
          }
        }
        setValue("loanInfoMeta.autoEstimateLoan", useAutoEstimateFromBalance);
        setUseMortgageBalance(useAutoEstimateFromBalance);

        setValue("main_company_name", mainCompanyName || "");
        setShowManualEditor(true);

        // Fetch title/escrow costs after address is set
        /*   const currentForm = getValues();
          // Construct form object with address data for fetchTitleEscrow
              const _formForTitleEscrow = {
                 ...currentForm,
                 state: res?.data?.closing_state || res?.data?.state_code || "",
                 city: res?.data?.city || "",
                 county: res?.data?.closing_county || res?.data?.county_name || "",
                 zip: postalCode || "",
                 addressMeta: {
                   ...currentForm?.addressMeta,
                   zip: postalCode || "",
                   state: res?.data?.state_code || "",
                   city: res?.data?.city || "",
                   county: res?.data?.county_name || "",
                   closing_county: res?.data?.closing_county || "",
                   closing_state: res?.data?.closing_state || "",
                 },
                 loanInfoMeta: {
                   ...currentForm?.loanInfoMeta,
                   estimatedClosingDate: defaultEst,
                   buyersLoanType: currentForm?.loanInfoMeta?.buyersLoanType || "Conventional",
                 },
               };
               await fetchTitleEscrow(_formForTitleEscrow);
       */
        // Quick mode: do not run updateDerivedFields + replaceArray here. That runs
        // after async ZIP returns and races with template apply — it overwrites
        // sellerClosingCostMeta / closingCostMeta / compensation / otherCost labels
        // and values. Totals still update on sales-price blur (normal seller path).
        await fetchSellerStateSettings(
          zipPayload.stateid ?? zipPayload.data?.stateid,
        );
        calculateTotals();
      } catch {
        showToast({
          message: "Unable to verify ZIP right now.",
          type: "error",
        });
      }
    })();
  }, [
    isQuickMode,
    defaultEst,
    setValue,
    showToast,
    mainCompanyName,
    postalCode,
    getValues,
    fetchSellerStateSettings,
    calculateTotals,
  ]);

  // existing calc load
  useEffect(() => {
    const load = async () => {
      if (!calcId) return;
      if (refs.loadedExistingRef.current === calcId) return;
      if (refs.loadingExistingRef.current) return;

      refs.loadingExistingRef.current = true;
      refs.isPopulatingRef.current = true;

      try {
        const data = await getSellerCalc(calcId);
        if (!data) return;

        setValue("salesPrice", String(data.salesPrice || "0.00"));
        setValue("preparedBy", data.preparedBy || "");
        setValue("preparedFor", data.preparedFor || "");
        setValue("addressMeta", data.addressMeta || {});
        setValue("loanInfoMeta", data.loanInfoMeta || {});
        setValue("titleSettlementMeta", data.titleSettlementMeta || {});
        setValue("totalClosingMeta", data.totalClosingMeta || {});

        if (Array.isArray(data.closingCostMeta))
          arrays.closingCost.replace(data.closingCostMeta);
        if (Array.isArray(data.sellerClosingCostMeta))
          arrays.sellerClosingCost.replace(data.sellerClosingCostMeta);
        if (Array.isArray(data.sellerOtherCostMeta))
          arrays.sellerOtherCost.replace(data.sellerOtherCostMeta);
        if (Array.isArray(data.compensationMeta))
          arrays.compensation.replace(data.compensationMeta);
        if (Array.isArray(data.otherCostMeta))
          arrays.otherCost.replace(data.otherCostMeta);

        setShowManualEditor(true);
        setIsInitialLoad(true);
        refs.loadedExistingRef.current = calcId;
      } catch (e) {
        console.warn("existing calc load failed", e);
      } finally {
        refs.loadingExistingRef.current = false;
        setTimeout(() => (refs.isPopulatingRef.current = false), 120);
      }
    };

    load();
  }, [calcId, setValue, arrays, refs]);

  // mortgage balance sync: autoEstimateLoan and form balance/rate depend on actual loan balance > 0
  useEffect(() => {
    if (refs.isPopulatingRef.current) return;

    const ttbBalance = ttbData?.loanBalance?.balance;
    const balanceNum = Number(ttbBalance);
    const hasValidBalance = balanceNum > 0;
    const useAutoEstimate = useMortgageBalance && hasValidBalance;

    setValue("loanInfoMeta.autoEstimateLoan", useAutoEstimate);
    // console.log("[Seller autoEstimateLoan] set by: mortgage sync ->", useAutoEstimate, "| useMortgageBalance:", useMortgageBalance, "| balance:", ttbBalance);

    if (useAutoEstimate) {
      setValue(
        "loanInfoMeta.loansToBePaidBalance1",
        ttbData?.loanBalance?.balance ?? "0.00",
      );
      setValue(
        "loanInfoMeta.loansToBePaidRate1",
        ttbData?.loanBalance?.interestRate ?? "0.00",
      );
    } else {
      const mort = getValues("loanInfoMeta.mortgageAmount") || "0.00";
      setValue("loanInfoMeta.loansToBePaidBalance1", mort);
    }
  }, [useMortgageBalance, ttbData, getValues, setValue, refs.isPopulatingRef]);

  // home warranty sync
  useEffect(() => {
    const closing = getValues("closingCostMeta") || [];
    const warrantyAmount =
      getValues("loanInfoMeta.homeWarrantyAmount") || "0.00";

    if (!useHomeWarranty) {
      setValue("loanInfoMeta.homeWarrantyAmount", "0.00");
      setValue(
        "closingCostMeta",
        closing.map((item) =>
          isHomeWarrantyItem(item)
            ? { ...item, value: "0.00", amount: 0, total: 0 }
            : item,
        ),
      );
      return;
    }

    let found = false;
    const updated = closing.map((item) => {
      if (isHomeWarrantyItem(item)) {
        found = true;
        const n = parseFloat(warrantyAmount) || 0;
        return {
          ...item,
          value: warrantyAmount,
          amount: n,
          total: n,
          type: item.type || "Flat Fee",
        };
      }
      return item;
    });

    if (!found) {
      const n = parseFloat(warrantyAmount) || 0;
      updated.push({
        key: "home_warranty",
        camelKey: "homeWarranty",
        label: "Home Warranty",
        value: warrantyAmount,
        amount: n,
        total: n,
        type: "Flat Fee",
        unit: "$",
      });
    }

    setValue("closingCostMeta", updated);
  }, [useHomeWarranty, watchedHomeWarrantyAmount, getValues, setValue]);

  // tax proration
  useEffect(() => {
    const form = getValues();
    const stateSettings = watchedStateSettings || form?.stateSettings || {};
    // Prioritize manually entered annualPropertyTaxAmount over ttbData.taxAmount
    // If user has manually entered a value that differs from ttbData, use the manual value
    const taxAmountFromForm = parseCurrency(
      form?.loanInfoMeta?.annualPropertyTaxAmount || 0,
    );
    const taxAmountFromTtb = Number(ttbData?.taxAmount || 0);

    // Use form value if it's been manually changed (different from ttbData), otherwise use ttbData if available
    let taxAmount = taxAmountFromForm;
    if (taxAmountFromForm <= 0 && taxAmountFromTtb > 0) {
      taxAmount = taxAmountFromTtb;
    } else if (taxAmountFromForm > 0) {
      // User has manually entered a value, use it
      taxAmount = taxAmountFromForm;
    }

    if (taxAmount <= 0 || !Object.keys(stateSettings).length) return;

    const estimatedTax = getSellerEstimatedTax(
      form?.loanInfoMeta?.estimatedClosingDate,
      taxAmount,
      stateSettings,
      "seller",
    );
    if (!estimatedTax || Number.isNaN(estimatedTax.estimatedTaxProrations))
      return;

    const prev = getValues("loanInfoMeta") || {};
    setValue("loanInfoMeta", {
      ...prev,
      estimatedTaxProrations: estimatedTax.estimatedTaxProrations,
    });
  }, [
    ttbData?.taxAmount,
    watchedStateSettings,
    watchedAnnualPropertyTax,
    watchedEstimatedClosingDate,
    getValues,
    setValue,
  ]);

  // annual property tax toggle -> sync amount field
  useEffect(() => {
    const existingLoan = getValues("loanInfoMeta") || {};
    setValue("loanInfoMeta", {
      ...existingLoan,
      annualPropertyTaxAmount: useAnnualPropertyTax
        ? (existingLoan.annualPropertyTax ?? 0)
        : 0,
    });
  }, [useAnnualPropertyTax, getValues, setValue]);

  const handleAddressEdited = useCallback((val) => {
    if (val?.length > 3) {
      //  console.log("[Seller autoEstimateLoan] set by: address edited", val);
      setIsCorrectProperty(true);
      setIsInitialLoad(false);
      setUseAnnualPropertyTax(true);
      // Don't assume loan balance until we have ttb data; keep false until enrichment
      setUseMortgageBalance(false);
      setUseHomeWarranty(false);
    }
  }, []);

  const handleSiteUnitChange = useCallback(
    (val) => {
      setValue("addressMeta.unitNumber", val || "");
      setValue("addressMeta.addressIncludesUnit", true);
    },
    [setValue],
  );

  const handleAddressEnriched = useCallback(
    async (data) => {
      //    console.log(
      //      "Address enrichment data received handleAddressEnriched :",
      //      data,
      //    );

      const { ttb, payload, raw, showPreview } = data || {};
      const propertyUnavailable =
        (!ttb || Object.keys(ttb || {}).length === 0) &&
        (!raw || Object.keys(raw || {}).length === 0);

      if (propertyUnavailable) {
        showToast({
          message:
            "The address you entered is incorrect or the State/County is not supported.",
          type: "error",
        });
        return;
      }

      if (ttb) setTtbData(ttb);

      const companyIdVal = payload?.companyid || null;

      //  console.log("companyIdVal :", companyIdVal);

      if (companyIdVal) {
        try {
          const tpl = await getUserTemplate(companyIdVal);
          const empty =
            tpl == null ||
            (Array.isArray(tpl) && tpl.length === 0) ||
            (typeof tpl === "object" && Object.keys(tpl).length === 0);

          setTemplateMissing(empty);
          if (empty) {
            showToast({
              message:
                "The zip code you entered is incorrect or the State/County is not supported.",
              type: "error",
            });
          }
        } catch (e) {
          console.warn("getUserTemplate failed", e);
        }
      }

      if (payload) {
        const prevAddr = getValues("addressMeta") || {};
        const oldmrsid = prevAddr?.mrsid || "";
        const timestamp = Date.now();

        // Construct formatted address from components if not provided
        const formattedAddress =
          payload.formatted_address ||
          (payload.street_no && payload.route_long
            ? `${payload.street_no} ${payload.route_long}${payload.site_unit ? " #" + payload.site_unit : ""}, ${payload.city}, ${payload.state_code} ${payload.zip}`
            : "");

        const resolvedStateId =
          (payload.stateid != null && String(payload.stateid).trim() !== ""
            ? String(payload.stateid).trim()
            : "") ||
          String(
            raw?.stateid ||
            raw?.data?.stateid ||
            raw?.ttb?.stateid ||
            prevAddr.stateid ||
            "",
          ).trim();

        setValue("loanInfoMeta.estimatedClosingDate", defaultEst);
        setValue("loanInfoMeta.annualPropertyTax", ttb?.taxAmount);
        setValue("loanInfoMeta.annualPropertyTaxAmount", ttb?.taxAmount);
        setValue("addressMeta", {
          ...prevAddr,
          companyid: payload?.companyid || prevAddr.companyid || "",
          address: formattedAddress || prevAddr.address || "",
          street_no: payload.street_no ?? prevAddr.street_no ?? "",
          street_name:
            payload.route_long || payload.route || prevAddr.street_name || "",
          city: payload.city || prevAddr.city || "",
          state: payload.state_code || prevAddr.state || "",
          stateid: resolvedStateId || prevAddr.stateid || "",
          county:
            payload?.county_name || prevAddr.county || "",
          countyid: payload?.countyid || prevAddr.countyid || "",
          zip: payload.zip || prevAddr.zip || "",
          unitNumber: payload.site_unit ?? prevAddr.unitNumber ?? "",
          addressIncludesUnit: !!(
            (payload.site_unit && String(payload.site_unit).trim()) ||
            prevAddr.addressIncludesUnit
          ),
          closing_county:
            payload?.closing_county || prevAddr.closing_county || "",
          closing_state: payload?.closing_state || prevAddr.closing_state || "",
          mrsid: oldmrsid ? oldmrsid : `${timestamp}`,
        });

        setValue("main_company_name", mainCompanyName || "");
        setValue(
          "loanInfoMeta.loansToBePaidBalance1",
          ttb?.loanBalance?.balance || "0.00",
        );
        setValue(
          "loanInfoMeta.loansToBePaidRate1",
          ttb?.loanBalance?.interestRate || "0.00",
        );
        // Set autoEstimateLoan / useMortgageBalance only when loan balance > 0
        const bal = ttb?.loanBalance?.balance ?? ttb?.loanBalance;
        const useAutoFromEnrichment = Number(bal) > 0;
        setValue("loanInfoMeta.autoEstimateLoan", useAutoFromEnrichment);
        setUseMortgageBalance(useAutoFromEnrichment);
        // setValue("mrs_id", `${timestamp}`);
      }

      const stateIdForSettings =
        (payload?.stateid != null && String(payload.stateid).trim() !== ""
          ? String(payload.stateid).trim()
          : null) ||
        String(raw?.stateid || raw?.data?.stateid || raw?.ttb?.stateid || "")
          .trim() ||
        (typeof getValues === "function"
          ? String(getValues("addressMeta.stateid") || "").trim()
          : "") ||
        undefined;

      const formAfterAddress = getValues() || {};
      const spAfter = parseCurrency(formAfterAddress.salesPrice);
      if (spAfter > 0) {
        await fetchTitleEscrow(formAfterAddress);
      }
      await fetchSellerStateSettings(stateIdForSettings);
      applyDerivedCostRows();

      setShowPreviewFromChild(Boolean(showPreview));
      setIsInitialLoad(Boolean(showPreview));
    },
    [
      defaultEst,
      setValue,
      showToast,
      mainCompanyName,
      getValues,
      fetchSellerStateSettings,
      fetchTitleEscrow,
      applyDerivedCostRows,
    ],
  );

  const handleManualZipVerified = useCallback(
    async (info) => {
      const sid =
        info?.stateid != null && String(info.stateid).trim() !== ""
          ? String(info.stateid).trim()
          : undefined;
      await fetchSellerStateSettings(sid);
      const form = getValues() || {};
      if (parseCurrency(form.salesPrice) > 0) {
        await fetchTitleEscrow(form);
      }
      applyDerivedCostRows();
    },
    [
      fetchSellerStateSettings,
      fetchTitleEscrow,
      getValues,
      applyDerivedCostRows,
    ],
  );

  const handleNewCalcLocal = useCallback(async () => {
    setTtbData({});
    setTemplateMissing(false);
    setIsCorrectProperty(false);
    setShowPreviewFromChild(false);
    setIsInitialLoad(false);
    setShowManualEditor(false);

    setUseAnnualPropertyTax(true);
    setUseMortgageBalance(false);
    setUseHomeWarranty(false);

    reset();
    refs.loadedExistingRef.current = null;
    refs.isPopulatingRef.current = false;
  }, [reset, refs]);

  return {
    // refs
    containerRef,
    // state
    ttbData,
    templateMissing,
    showPreviewFromChild,
    showManualEditor,
    showAddressView,
    isCorrectProperty,
    isInitialLoad,
    useMortgageBalance,
    useAnnualPropertyTax,
    useHomeWarranty,

    // setters
    setShowManualEditor,
    setShowPreviewFromChild,
    setIsCorrectProperty,
    setIsInitialLoad,
    setUseMortgageBalance,
    setUseAnnualPropertyTax,
    setUseHomeWarranty,

    // handlers
    handleSalesPriceBlur,
    calculateTotals,
    handleAddressEnriched,
    handleSiteUnitChange,
    handleAddressEdited,
    handleNewCalcLocal,

    fetchSellerStateSettings,
    applyDerivedCostRows,
    handleManualZipVerified,
  };
};
